import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { mediaUrl } from "@/lib/api";
import DOMPurify from "dompurify";
import NewsCard from "@/components/public/NewsCard";
import AdSlot from "@/components/public/AdSlot";
import Comments, { ShareBar, ReadMore } from "@/components/public/Comments";
import { Eye, ArrowLeft, Calendar } from "lucide-react";

const clean = (html) => DOMPurify.sanitize(html || "", { ADD_ATTR: ["target"], FORBID_TAGS: ["style", "script", "iframe", "form"] });

export default function Article() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [latest, setLatest] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setArticle(null);
    setNotFound(false);
    api.get(`/news/${slug}`)
      .then((r) => {
        setArticle(r.data);
        return api.get("/news", { params: { category: r.data.category, limit: 4 } });
      })
      .then((r) => r && setRelated(r.data.items.filter((a) => a.slug !== slug).slice(0, 3)))
      .catch(() => setNotFound(true));
    api.get("/news", { params: { limit: 5 } }).then((r) => setLatest(r.data.items.filter((a) => a.slug !== slug).slice(0, 4))).catch(() => {});
  }, [slug]);

  if (notFound) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-24 text-center">
        <h1 className="text-3xl font-bold text-white">Article not found</h1>
        <Link to="/news" className="mt-4 inline-block text-[hsl(var(--primary))]">← Back to News</Link>
      </div>
    );
  }
  if (!article) return <div className="py-24 text-center text-white/40">Loading…</div>;

  return (
    <div>
      <div className="relative h-[42vh] min-h-[320px] overflow-hidden">
        <img src={mediaUrl(article.cover)} alt={article.title} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[hsl(var(--background))] via-black/50 to-black/20" />
        <div className="relative z-10 h-full mx-auto max-w-4xl px-4 md:px-6 flex flex-col justify-end pb-8">
          <Link to="/news" className="eyebrow text-white/60 hover:text-white flex items-center gap-1.5 mb-4"><ArrowLeft size={13} /> Back</Link>
          <span className="eyebrow self-start px-2.5 py-1 rounded" style={{ background: "hsl(var(--accent))" }}>{article.category}</span>
          <h1 className="mt-4 text-3xl md:text-5xl font-bold text-white leading-tight max-w-3xl" data-testid="article-title">{article.title}</h1>
          <div className="mt-4 flex items-center gap-5 text-sm text-white/60">
            <span>{article.author}</span>
            <span className="flex items-center gap-1.5"><Calendar size={14} /> {new Date(article.created_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</span>
            <span className="flex items-center gap-1.5"><Eye size={14} /> {article.views?.toLocaleString()} views</span>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-4xl px-4 md:px-6 py-10 grid lg:grid-cols-[1fr_300px] gap-10">
        <article className="min-w-0">
          {article.excerpt && <p className="text-lg text-white/70 leading-relaxed">{article.excerpt}</p>}
          <div className="mt-6 article-content" data-testid="article-content" dangerouslySetInnerHTML={{ __html: clean(article.content) }} />
          {article.tags?.length > 0 && (
            <div className="mt-8 flex flex-wrap gap-2">
              {article.tags.map((t) => <span key={t} className="px-3 py-1 rounded-full card-surface text-xs text-white/60">#{t}</span>)}
            </div>
          )}
          <div className="mt-8 pt-6 border-t border-white/10"><ShareBar title={article.title} /></div>
          <Comments slug={slug} />
        </article>
        <aside>
          <AdSlot className="mb-6" style={{ minHeight: 250 }} label="Advertisement" />
          <h3 className="text-sm font-bold text-white/70 mb-3">Related</h3>
          <div className="space-y-3">{related.map((a) => <NewsCard key={a.id} article={a} variant="row" />)}</div>
        </aside>
      </div>
      <ReadMore items={latest} />
    </div>
  );
}
